import React from 'react';
import './LegalPages.css';

const PrivacyPolicy = () => {
  return (
    <div className="legal-page">
      <section className="legal-hero">
        <h1 className="display-large">Privacy Policy</h1>
        <p className="body-medium" style={{ marginTop: '24px', color: 'var(--text-muted)' }}>
          Last Updated: January 17, 2025
        </p>
      </section>

      <section className="legal-content">
        <div className="legal-text">
          <h2 className="heading-1">1. Introduction</h2>
          <p className="body-medium">
            EAET LLC ("Company", "we", "us", or "our"), a Delaware limited liability company located at Wilmington, DE 19801, USA, respects your privacy and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI agent platform and services.
          </p>

          <h2 className="heading-1">2. Information We Collect</h2>
          
          <h3 className="heading-3">2.1 Information You Provide</h3>
          <ul className="body-medium legal-list">
            <li><strong>Account Information:</strong> Name, email address, company name, phone number, billing address</li>
            <li><strong>Payment Information:</strong> Credit card details, billing information (processed by third-party payment processors)</li>
            <li><strong>Profile Data:</strong> Job title, department, preferences, profile picture</li>
            <li><strong>Communications:</strong> Messages, support tickets, feedback, and other communications with us</li>
            <li><strong>Agent Configuration Data:</strong> Workflows, integrations, and custom agent settings you create</li>
          </ul>

          <h3 className="heading-3">2.2 Information We Collect Automatically</h3>
          <ul className="body-medium legal-list">
            <li><strong>Usage Data:</strong> How you interact with our Platform, features used, time spent, actions taken</li>
            <li><strong>Device Information:</strong> IP address, browser type, operating system, device identifiers</li>
            <li><strong>Log Data:</strong> Access times, pages viewed, error logs, referring URLs</li>
            <li><strong>Cookies and Tracking:</strong> We use cookies and similar technologies to track activity and preferences</li>
          </ul>

          <h3 className="heading-3">2.3 Information from Third Parties</h3>
          <p className="body-medium">
            We may receive information from third-party integrations you connect to our Platform, analytics providers, and marketing partners.
          </p>

          <h2 className="heading-1">3. How We Use Your Information</h2>
          <p className="body-medium">
            We use collected information for the following purposes:
          </p>
          <ul className="body-medium legal-list">
            <li><strong>Service Delivery:</strong> Provide, maintain, and improve our Platform and AI agents</li>
            <li><strong>Account Management:</strong> Create and manage your account, authenticate users</li>
            <li><strong>Payment Processing:</strong> Process transactions and send billing information</li>
            <li><strong>Customer Support:</strong> Respond to inquiries, provide technical support, resolve issues</li>
            <li><strong>Platform Improvement:</strong> Analyze usage patterns, develop new features, enhance user experience</li>
            <li><strong>Security:</strong> Detect fraud, prevent abuse, ensure Platform security</li>
            <li><strong>Communications:</strong> Send service updates, security alerts, marketing communications (with consent)</li>
            <li><strong>Legal Compliance:</strong> Comply with legal obligations and enforce our Terms of Service</li>
          </ul>

          <h2 className="heading-1">4. How We Share Your Information</h2>
          <p className="body-medium">
            We do not sell your personal information. We may share your information with:
          </p>
          <ul className="body-medium legal-list">
            <li><strong>Service Providers:</strong> Third-party vendors who perform services on our behalf (cloud hosting, payment processing, analytics)</li>
            <li><strong>Business Transfers:</strong> In connection with mergers, acquisitions, or sale of assets</li>
            <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
            <li><strong>With Your Consent:</strong> When you explicitly authorize sharing</li>
            <li><strong>Aggregated Data:</strong> De-identified, aggregated data that cannot identify you individually</li>
          </ul>

          <h2 className="heading-1">5. Data Security</h2>
          <p className="body-medium">
            We implement industry-standard security measures to protect your information:
          </p>
          <ul className="body-medium legal-list">
            <li>End-to-end encryption for data in transit and at rest</li>
            <li>SOC 2 Type II certified infrastructure</li>
            <li>Regular security audits and penetration testing</li>
            <li>Access controls and authentication mechanisms</li>
            <li>Employee training on data protection practices</li>
          </ul>
          <p className="body-medium">
            However, no security system is impenetrable. We cannot guarantee absolute security of your information.
          </p>

          <h2 className="heading-1">6. Data Retention</h2>
          <p className="body-medium">
            We retain your personal information for as long as necessary to provide our services and comply with legal obligations. When data is no longer needed, we securely delete or anonymize it.
          </p>
          <ul className="body-medium legal-list">
            <li><strong>Account Data:</strong> Retained while your account is active and for a reasonable period after closure</li>
            <li><strong>Transaction Records:</strong> Retained for 7 years for accounting and tax purposes</li>
            <li><strong>Communications:</strong> Retained as needed for customer support and legal compliance</li>
          </ul>

          <h2 className="heading-1">7. Your Privacy Rights</h2>
          <p className="body-medium">
            Depending on your location, you may have the following rights:
          </p>
          <ul className="body-medium legal-list">
            <li><strong>Access:</strong> Request a copy of personal information we hold about you</li>
            <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data</li>
            <li><strong>Deletion:</strong> Request deletion of your personal information</li>
            <li><strong>Portability:</strong> Request transfer of your data to another service</li>
            <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
            <li><strong>Restriction:</strong> Request restriction of processing in certain circumstances</li>
            <li><strong>Object:</strong> Object to processing based on legitimate interests</li>
          </ul>
          <p className="body-medium">
            To exercise these rights, contact us through our contact form. We will respond within 30 days.
          </p>

          <h2 className="heading-1">8. Cookies and Tracking Technologies</h2>
          <p className="body-medium">
            We use cookies and similar technologies to:
          </p>
          <ul className="body-medium legal-list">
            <li><strong>Essential Cookies:</strong> Required for Platform functionality and security</li>
            <li><strong>Analytics Cookies:</strong> Help us understand how users interact with our Platform</li>
            <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
            <li><strong>Marketing Cookies:</strong> Deliver relevant advertisements (with consent)</li>
          </ul>
          <p className="body-medium">
            You can control cookies through your browser settings. Disabling certain cookies may affect Platform functionality.
          </p>

          <h2 className="heading-1">9. International Data Transfers</h2>
          <p className="body-medium">
            Your information may be transferred to and processed in the United States and other countries where we operate. We ensure appropriate safeguards are in place for international transfers, including:
          </p>
          <ul className="body-medium legal-list">
            <li>Standard Contractual Clauses approved by regulatory authorities</li>
            <li>Adequacy decisions by relevant data protection authorities</li>
            <li>Data processing agreements with vendors</li>
          </ul>

          <h2 className="heading-1">10. Children's Privacy</h2>
          <p className="body-medium">
            Our Platform is not intended for users under 18 years of age. We do not knowingly collect personal information from children. If we learn we have collected information from a child, we will delete it immediately.
          </p>

          <h2 className="heading-1">11. Third-Party Links and Services</h2>
          <p className="body-medium">
            Our Platform may contain links to third-party websites and integrate with third-party services. We are not responsible for their privacy practices. We encourage you to review their privacy policies.
          </p>

          <h2 className="heading-1">12. California Privacy Rights (CCPA)</h2>
          <p className="body-medium">
            California residents have additional rights under the California Consumer Privacy Act:
          </p>
          <ul className="body-medium legal-list">
            <li>Right to know what personal information is collected, used, shared, or sold</li>
            <li>Right to delete personal information</li>
            <li>Right to opt-out of the sale of personal information (we do not sell personal information)</li>
            <li>Right to non-discrimination for exercising CCPA rights</li>
          </ul>

          <h2 className="heading-1">13. European Privacy Rights (GDPR)</h2>
          <p className="body-medium">
            If you are in the European Economic Area (EEA), you have rights under the General Data Protection Regulation (GDPR). We process your data based on:
          </p>
          <ul className="body-medium legal-list">
            <li><strong>Contract Performance:</strong> Necessary to provide our services</li>
            <li><strong>Legitimate Interests:</strong> For business operations and improvements</li>
            <li><strong>Legal Obligations:</strong> To comply with applicable laws</li>
            <li><strong>Consent:</strong> Where you have provided explicit consent</li>
          </ul>

          <h2 className="heading-1">14. Changes to This Privacy Policy</h2>
          <p className="body-medium">
            We may update this Privacy Policy periodically. We will notify you of material changes via email or through the Platform. The "Last Updated" date at the top indicates when the policy was last revised.
          </p>

          <h2 className="heading-1">15. Contact Us</h2>
          <p className="body-medium">
            For questions, concerns, or to exercise your privacy rights, please use our contact form.
          </p>
          <div className="contact-info">
            <p className="body-medium"><strong>EAET LLC</strong></p>
            <p className="body-medium">Wilmington, DE 19801</p>
            <p className="body-medium">United States of America</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
